<template>
    <div class="enps-period">
        <!-- Тулбар: год, «Весь год» и селект отделов. Кварталов здесь больше нет — их подписи
             стоят прямо над своими тройками месяцев ниже: шорткат живёт на том, чем управляет,
             а не в оторванном ряду. -->
        <div class="enps-period__toolbar">
            <button
                v-for="option in yearOptions"
                :key="option"
                type="button"
                class="enps-period__pill enps-period__pill_year"
                :class="{ 'enps-period__pill_active': option === year }"
                @click="selectYear(option)"
            >
                {{ option }}
            </button>

            <span class="enps-period__divider"></span>

            <button
                type="button"
                class="enps-period__pill"
                :class="{ 'enps-period__pill_active': isWholeYear }"
                @click="toggleWholeYear"
            >
                Весь год
            </button>

            <!-- Сброс к периоду по умолчанию (текущий месяц). Показывается только когда выбор
                 от него отличается: кнопка, которой нечего сбрасывать, — визуальный шум. -->
            <button v-if="!isDefaultSelection" type="button" class="enps-period__reset" @click="resetToDefault">
                Сбросить
            </button>

            <!-- Правый край тулбара — сюда страница кладёт админский селект отделов;
                 компонент таблеток про отделы ничего не знает. -->
            <div class="enps-period__aside">
                <slot name="aside"></slot>
            </div>
        </div>

        <!-- Месяцы группами по кварталам; подпись квартала кликабельна и включает/выключает
             свою тройку, подсвечиваясь, когда выбраны все три месяца. -->
        <div class="enps-period__quarters">
            <div v-for="quarter in quarters" :key="quarter.key" class="enps-period__quarter">
                <button
                    type="button"
                    class="enps-period__quarter-caption"
                    :class="{ 'enps-period__quarter-caption_active': isQuarterActive(quarter) }"
                    title="Клик — выбрать квартал; Ctrl+клик — добавить/убрать"
                    @click="toggleQuarter(quarter, $event)"
                >
                    {{ quarter.label }}
                </button>
                <div class="enps-period__quarter-months">
                    <button
                        v-for="month in quarter.months"
                        :key="month"
                        type="button"
                        class="enps-period__pill enps-period__pill_month"
                        :class="{ 'enps-period__pill_active': months.includes(month) }"
                        title="Клик — выбрать месяц; Ctrl+клик — добавить/убрать"
                        @click="toggleMonth(month, $event)"
                    >
                        {{ monthNames[month - 1] }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import ruMonthNames from '@/js/ruMonthNames';
import { QUARTERS } from '@/js/enpsPeriod';

// Оценки появились в системе в 2026 году (create_request_ratings_table) — таблетки более
// ранних лет были бы мёртвыми кнопками с гарантированно пустым дашбордом.
const FIRST_YEAR = 2026;
const MAX_YEARS_SHOWN = 5;

// Выбор периода дашборда: год — один, месяцы — по модели файлового менеджера: обычный клик
// ВЫБИРАЕТ ровно этот месяц/квартал (заменяя выбор), Ctrl/Cmd+клик — мультиселект (добавляет/
// убирает). Кварталы и «Весь год» — шорткаты над месяцами: собственного состояния у них нет,
// активными они выглядят ровно тогда, когда выбраны все их месяцы.
//
// Компонент управляемый: год и месяцы приходят пропсами, наружу уходит только событие change.
// Своей копии состояния он не держит намеренно — иначе после отката к дефолту (см. ниже) или
// восстановления периода из адресной строки таблетки и запрос разъезжались бы.
//
// Смена года НЕ сбрасывает выбранные месяцы: выбрав «Январь», по годам можно щёлкать как по
// вкладкам, сравнивая один и тот же месяц год к году.
@Component
class EnpsPeriodPills extends Vue {
    @Prop({ type: Number, required: true })
    year;

    @Prop({ type: Array, required: true })
    months;

    monthNames = ruMonthNames;
    quarters = QUARTERS;

    // С FIRST_YEAR по текущий год, но не больше MAX_YEARS_SHOWN: пока лет мало — ровно они
    // ([2026], потом [2026, 2027], ...), а с шестого года окно скользит вперёд, отбрасывая
    // самый старый год.
    get yearOptions() {
        const currentYear = new Date().getFullYear();
        const startYear = Math.max(FIRST_YEAR, currentYear - MAX_YEARS_SHOWN + 1);

        return Array.from({ length: currentYear - startYear + 1 }, (unused, index) => startYear + index);
    }

    get isWholeYear() {
        return this.months.length === 12;
    }

    // Выбор совпадает с дефолтом дашборда — текущий месяц текущего года (см. initPeriod()
    // на странице): кнопке сброса в этом состоянии нечего делать, и она скрыта.
    get isDefaultSelection() {
        const now = new Date();

        return this.year === now.getFullYear() && this.months.length === 1 && this.months[0] === now.getMonth() + 1;
    }

    resetToDefault() {
        const now = new Date();

        this.emit(now.getFullYear(), [now.getMonth() + 1]);
    }

    isQuarterActive(quarter) {
        return quarter.months.every((month) => this.months.includes(month));
    }

    selectYear(year) {
        if (year === this.year) {
            return;
        }

        this.emit(year, this.months);
    }

    // Модель файлового менеджера: обычный клик заменяет выбор («только этот месяц»),
    // Ctrl/Cmd+клик — мультиселект, добавляет/убирает не трогая остального.
    toggleMonth(month, event) {
        if (!this.isMultiSelect(event)) {
            this.emit(this.year, [month]);
            return;
        }

        const months = this.months.includes(month)
            ? this.months.filter((value) => value !== month)
            : this.months.concat(month);

        this.emit(this.year, months);
    }

    toggleQuarter(quarter, event) {
        if (!this.isMultiSelect(event)) {
            this.emit(this.year, quarter.months.slice());
            return;
        }

        const months = this.isQuarterActive(quarter)
            ? this.months.filter((month) => !quarter.months.includes(month))
            : this.months.concat(quarter.months.filter((month) => !this.months.includes(month)));

        this.emit(this.year, months);
    }

    // metaKey — тот же жест на Mac (Cmd+клик).
    isMultiSelect(event) {
        return Boolean(event && (event.ctrlKey || event.metaKey));
    }

    toggleWholeYear() {
        const months = this.isWholeYear ? [] : Array.from({ length: 12 }, (unused, index) => index + 1);

        this.emit(this.year, months);
    }

    // Пустой выбор — не состояние дашборда, а промах: снять с себя последний месяц пользователь
    // может, но смотреть после этого не на что. Поэтому пустой набор заменяется текущим месяцем.
    emit(year, months) {
        const normalized = months.length > 0 ? months.slice().sort((a, b) => a - b) : [new Date().getMonth() + 1];

        this.$emit('change', { year, months: normalized });
    }
}

export default EnpsPeriodPills;
</script>
<style lang="scss">
// Намеренно БЕЗ карточной обёртки (тень и отступы убраны по решению владельца продукта):
// фильтр — часть страницы, а не ещё одна карточка среди виджетов.
.enps-period {
    margin-bottom: 20px;

    &__toolbar {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        margin-bottom: 10px;
    }

    &__divider {
        width: 1px;
        height: 18px;
        margin: 0 6px;
        background-color: #e4eaef;
    }

    // Текстовая кнопка, не чип: сброс — действие, а не состояние, и не должен выглядеть как
    // ещё одна таблетка выбора.
    &__reset {
        margin-left: 4px;
        padding: 5px 6px;
        border: none;
        background: none;
        color: #767676;
        font-size: 0.8125rem;
        text-decoration: underline;
        text-decoration-style: dashed;
        text-underline-offset: 3px;
        cursor: pointer;
        transition: color 0.15s;

        &:hover {
            color: #3c4858;
        }
    }

    &__quarters {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 14px;
    }

    &__quarter {
        flex: 1 1 0;
        min-width: 200px;
    }

    // Подпись квартала — «─ I кв ─»: линии по бокам собирают тройку месяцев в группу, сама
    // подпись — кнопка-шорткат (включает/выключает свои три месяца).
    &__quarter-caption {
        display: flex;
        align-items: center;
        width: 100%;
        gap: 8px;
        margin-bottom: 6px;
        padding: 0;
        border: none;
        background: none;
        color: #a0a0a0;
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        cursor: pointer;
        transition: color 0.15s;

        &::before,
        &::after {
            content: '';
            flex: 1;
            height: 1px;
            background-color: #e4eaef;
            transition: background-color 0.15s;
        }

        // Hover лишь затемняет серый — тёмно-жжёный (ACCENT_DEEP) занят активным состоянием,
        // одинаковый цвет делал их неразличимыми.
        &:hover {
            color: #3c4858;
        }

        &_active {
            color: #c05a1f;

            &::before,
            &::after {
                background-color: #f6c6a4;
            }

            &:hover {
                color: #c05a1f;
            }
        }
    }

    &__quarter-months {
        display: flex;
        gap: 6px;
    }

    &__aside {
        margin-left: auto;
        min-width: 220px;

        &:empty {
            display: none;
        }

        // Селект отделов внутри тулбара — компактный, под высоту таблеток: с фирменными
        // паддингами custom-select (16px) он раздувал весь ряд вдвое.
        .custom-select.v-select {
            .vs__dropdown-toggle {
                padding: 2px 8px;
                border-radius: 15px;
                border-color: #dbe3e9;
            }

            .vs__search,
            .vs__selected {
                font-size: 0.8125rem;
            }
        }
    }

    // Чипы без рамок: заливка вместо обводки — двенадцать обведённых кнопок в ряд рябили.
    // Корп-оранжевый — интерфейсный акцент ENPS (см. enpsColors.js): зелёный на дашборде
    // зарезервирован за семантикой «хорошая оценка».
    &__pill {
        padding: 6px 14px;
        border: none;
        border-radius: 15px;
        background-color: #f2f2f2;
        color: #3c4858;
        font-size: 0.8125rem;
        line-height: 1.4;
        cursor: pointer;
        transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;

        // Hover нейтральный (чуть темнее серый), НЕ оранжевый: полутон акцента занят активным
        // состоянием, и оранжевый hover был бы от него неотличим.
        &:hover {
            background-color: #e6e6e6;
        }

        // Год — единственный «одиночный» выбор в блоке, выделен насыщенностью шрифта.
        &_year {
            font-weight: 600;
        }

        // Месяцы делят ширину карточки поровну — единый контрол, а не россыпь кнопок.
        &_month {
            flex: 1 1 0;
            min-width: 52px;
            text-align: center;
        }

        // Массовые активные состояния — ПОЛУТОН (при «Весь год» горят все 12 месяцев, сплошная
        // заливка превращала строку в оранжевое поле); сплошной акцент — только у активного
        // года ниже: одно сильное пятно-якорь на блок.
        &_active {
            background-color: #fdeadd;
            color: #c05a1f;
            font-weight: 600;

            &:hover {
                background-color: #fdeadd;
                color: #c05a1f;
            }
        }

        &_year#{&}_active {
            background-color: #f1905b;
            color: #fff;

            &:hover {
                background-color: #f1905b;
                color: #fff;
            }
        }
    }
}
</style>
